import React, { FC, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperClass } from 'swiper'
import { Fancybox } from '@fancyapps/ui'

import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/scrollbar'
import 'swiper/css/thumbs'
import '@fancyapps/ui/dist/fancybox/fancybox.css'

import { FreeMode, Navigation, Scrollbar, Thumbs } from 'swiper/modules'
import { ChevronNextIcon, ChevronPrevIcon, SearchIcon } from '@svgs'
import { useTranslation } from 'react-i18next'

interface IImageGallery {
  images: string[]
}

const ImageGallery: FC<IImageGallery> = ({ images }) => {
  const { t } = useTranslation()
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null)

  const thumbnails = images || []

  Fancybox.bind('[data-fancybox="gallery"]')

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-[29.75rem] bg-track rounded-xl flex items-center justify-center">
        <span className="text-ink-2">{t('no_images', 'No images available')}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Swiper
        navigation={{
          nextEl: '.gallery-swiper-button-next',
          prevEl: '.gallery-swiper-button-prev',
        }}
        loop={true}
        spaceBetween={10}
        speed={700}
        thumbs={thumbsSwiper ? { swiper: thumbsSwiper } : undefined}
        modules={[FreeMode, Navigation, Thumbs]}
        className="w-full relative rounded-xl overflow-hidden !h-[29.75rem] "
      >
        {thumbnails.map((slide, index) => (
          <SwiperSlide className="relative group" key={index}>
            <a href={slide} data-fancybox="gallery">
              <img className="object-cover size-full" src={slide} alt="" />
            </a>

            <div className="absolute inset-0 z-10 bg-black bg-opacity-50 flex flex-col items-center justify-center p-5 pointer-events-none group-hover:opacity-100 duration-300 transition-all opacity-0">
              <div className="flex flex-col items-center text-center gap-5">
                <div className="icon-button fill-white w-[2rem] h-[2rem]">
                  <SearchIcon />
                </div>
                <div className="text-center text-white text-sm font-normal">
                  {t('open_in_full_screen')}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
        <div className="absolute top-0 left-0 size-full pointer-events-none flex justify-between z-20">
          <button className="relative pl-4 group/arrow pr-3.5 h-full inline-flex items-center justify-center pointer-events-auto gallery-swiper-button-prev shrink-0 self-stretch fill-white hover:fill-white hover:from-[rgba(0,_0,_0,_1)] disabled:pointer-events-none disabled:opacity-0">
            <span className="icon-button w-6 h-6 relative z-10">
              <ChevronPrevIcon />
            </span>
            <strong className="bg-gradient-to-r from-black to-transparent transition-all duration-300 opacity-30 group-hover/arrow:opacity-80 absolute inset-0" />
          </button>
          <button className="relative pr-4 group/arrow pl-3.5 h-full inline-flex items-center justify-center pointer-events-auto gallery-swiper-button-next shrink-0 self-stretch fill-white hover:fill-white hover:from-[rgba(0,_0,_0,_1)] disabled:pointer-events-none disabled:opacity-0">
            <span className="icon-button w-6 h-6 relative z-10">
              <ChevronNextIcon />
            </span>
            <strong className="bg-gradient-to-l from-black to-transparent transition-all duration-300 opacity-30 group-hover/arrow:opacity-80 absolute inset-0" />
          </button>
        </div>
      </Swiper>
      <Swiper
        scrollbar
        onSwiper={setThumbsSwiper}
        speed={700}
        spaceBetween={8}
        slidesPerView={'auto'}
        freeMode={true}
        watchSlidesProgress={true}
        modules={[FreeMode, Navigation, Thumbs, Scrollbar]}
        className="h-[5.875rem] w-full pb-6 swiper-bottom-scrollbar"
      >
        {thumbnails.map((slide, index) => (
          <SwiperSlide
            className="h-full !w-[6.3125rem] rounded-lg overflow-hidden [&.swiper-slide-thumb-active]:opacity-50 transition-opacity cursor-pointer"
            key={index}
          >
            <img className="object-cover size-full" src={slide} alt="" />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

export default ImageGallery
